import { formatData } from '@utilities';

import { getColumnAccessor } from '@api/survey_dashboard/survey_responses/utilities/columns';

import { getSignificanceRowKey } from './significanceAdapter';
import styles from './significanceColumns.module.scss';

// Mirrors TableData's default cell renderer (`transformColumns` in
// @utilities/components/TableData/utilities/helpers.js): the NaN sentinel, null,
// and undefined render as '-'; a formatless column returns String(value) so a
// value like 0.4 keeps its text instead of being rounded to "0"; otherwise
// formatData applies the column format.
const renderDefaultCell = (column, value) => {
  if (value === 'NAN_SENTINEL' || value === null || value === undefined) return '-';
  if (!column.format) return String(value);
  return formatData({ format: column.format, value });
};

/**
 * Adds statistical-significance notation to report table column definitions.
 *
 * Eligible segment headers receive a comparison letter, and significant cells
 * receive superscript letters identifying the columns whose values are lower.
 * Rows are matched by stable row identity rather than display index so sorting
 * cannot misattribute results. The superscript text is supplied through CSS to
 * keep significance letters out of copied table content.
 *
 * @param {Object} options - Significance-column configuration.
 * @param {Array<Object>} options.columns - Table column definitions to transform.
 * @param {Array<string>} options.segmentAccessors - Accessors for comparable segment columns.
 * @param {Object<string, number>} options.statRowByKey - Statistical row indexes by stable row key.
 * @param {Object|null} options.statTest - Statistical test results, or null when unavailable.
 * @returns {Array<Object>} The transformed columns, or the original columns when no test is available.
 */
export const applySignificanceColumns = ({ columns, segmentAccessors, statRowByKey, statTest }) => {
  if (!statTest || !columns?.length) return columns;
  const { cellHigherThan, columnLetters } = statTest;

  return columns.map((column) => {
    const groupIndex = segmentAccessors.indexOf(getColumnAccessor(column));
    if (groupIndex === -1) return column; // option-label / non-segment column

    const letter = columnLetters[groupIndex];
    const originalCell = column.Cell;

    // Both the letters (cell content) and the full-cell tint (getCellHighlight)
    // resolve significance from the SAME stable row identity, so a client-side
    // sort can never let the tint and the letters disagree.
    const statRowFor = (rowOriginal) => statRowByKey?.[getSignificanceRowKey(rowOriginal)];
    const lettersFor = (statRow) =>
      statRow == null ? [] : (cellHigherThan?.[statRow]?.[groupIndex] ?? []);

    return {
      ...column,
      Cell: (cellProps) => {
        // Fall back to TableData's default cell rendering for missing cells, so
        // null/undefined/sentinel render as '-' and a formatless value keeps its
        // raw text instead of being rounded by formatData's default format.
        const base = originalCell
          ? originalCell(cellProps)
          : renderDefaultCell(column, cellProps?.value);
        const statRow = statRowFor(cellProps?.row?.original);
        const letters = lettersFor(statRow);
        if (!letters.length) return base;
        return (
          <span className={styles['significance-cell']}>
            {base}
            <sup
              aria-label={`significantly higher than ${letters.join(', ')}`}
              className={styles['significance-letters']}
              data-letters={letters.join(letters.some((l) => l.length > 1) ? ' ' : '')}
              data-testid={`significance-letters-${statRow}-${groupIndex}`}
            />
          </span>
        );
      },
      // Paint the whole <td> with the success tint (TableData's `is-success`)
      // when the cell is significant — mirrors Cardify's full-cell
      // `card-table-cell-positive`, keyed by the same identity as the letters.
      getCellHighlight: ({ row }) => (lettersFor(statRowFor(row)).length ? 'success' : undefined),
      Header:
        letter && typeof column.Header === 'string'
          ? `${column.Header} (${letter})`
          : column.Header,
    };
  });
};
